import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { addQuery, removeQuery } from 'Helpers/Query';
import fr from 'facet-rest';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { useState } from 'react';
import { downloadSecureFileFromLink } from 'Helpers/File';
import { pickFunction } from 'Helpers/Affordances';
import useUserContext from 'Context/UserContext';

function useAttachment(attachmentResource) {
  const {
    rootNodeProps,
    logResourceEvent,
  } = useEventTrackingLogEvent();
  const { userResource } = useUserContext();
  const secureFileRelationship = attachmentResource.relationship(fr.relationships.sendToSecureFiles.name) ? attachmentResource.relationship(fr.relationships.sendToSecureFiles.name) : attachmentResource.relationship(fr.relationships.secure.name);

  const [sendingToSecureFiles, setSendingToSecureFiles] = useState(false);

  const onClick = async (fileResource) => {
    addQuery({ attachmentSelfLink: fileResource.link('self').url() });
  };

  const handleLegacyDownload = async () => {
    const eventName = 'Clicked Download Attachment';
    logResourceEvent(eventName, attachmentResource);

    const link = document.createElement('a');
    link.download = attachmentResource.attr('name');
    link.target = '_blank';
    link.href = attachmentResource.relationship('download').link('related').url();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClick = pickFunction(userResource, {
    [fr.relationships.default.name]: () => onClick,
  });

  const handleDownload = attachmentResource.withRelationship(
    fr.relationships.download.name,
    (rel) => () => {
      const eventName = 'Clicked Download Attachment';
      logResourceEvent(eventName, attachmentResource);

      downloadSecureFileFromLink(rel.link('related'), attachmentResource.attr('name'), handleLegacyDownload);
    },
    () => undefined,
  );


  const handleSendToSecureFiles = async () => {
    logResourceEvent('Messages: Clicked Send to Secure Files', attachmentResource);

    if (!secureFileRelationship) {
      return;
    }

    setSendingToSecureFiles(true);
    try {
      const [resource] = await secureFileRelationship.link('related').get();
      await resource.submit({
        exportArgs: {
          exportAttributesAsIs: true,
        },
      });
      displaySuccess(`Moved ${attachmentResource.attr('name')} to secure files`);
    } catch (err) {
      displayErrorMessage(err, err.errors);
      throw err;
    } finally {
      setSendingToSecureFiles(false);
    }
  };

  const handleShowThread = async () => {
    logResourceEvent('Messages: Clicked Show File Thread', attachmentResource);

    if (!attachmentResource.relationship(fr.relationships.thread.name)) {
      return;
    }

    // Close any open modals and set the thread self link which will trigger the scroll to
    removeQuery('modal');
    addQuery({ threadSelfLink: attachmentResource.relationship(fr.relationships.thread.name).link('related').url() });
  };

  return {
    rootNodeProps,
    onClick: handleClick,
    onDownload: attachmentResource.relationship(fr.relationships.presentsAsDownloadable.name) && handleDownload,
    onSendToSecureFiles: secureFileRelationship && handleSendToSecureFiles,
    onShowThread: attachmentResource.relationship(fr.relationships.thread.name) && handleShowThread,
    sendingToSecureFiles,
  };
}

export default useAttachment;
